import { useEffect, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, useLocation, useNavigate, useParams } from 'react-router';
import { MeetingRecorderProvider } from './state/meeting-recorder.js';
import { CompanionProvider } from './state/companion.js';
import { EmbedSignIn, EmbedStatus, useEmbed } from './components/Embed.js';
import { Layout } from './components/Layout.js';
import { FeedbackOverlay } from './components/FeedbackOverlay.js';
import { Spinner } from './components/ui.js';
import { onUnauthorized } from './lib/api.js';
import { ContextPage } from './pages/ContextPage.js';
import { ConversationPage } from './pages/ConversationPage.js';
import { HomePage } from './pages/HomePage.js';
import { ProjectsPage } from './pages/ProjectsPage.js';
import { KnowsPage } from './pages/KnowsPage.js';
import { LoginPage } from './pages/LoginPage.js';
import { PublicPolicyPage } from './pages/PublicPolicyPage.js';
import { MemoryPage } from './pages/MemoryPage.js';
import { NewProjectPage } from './pages/NewProjectPage.js';
import { NotFoundPage } from './pages/NotFoundPage.js';
import { OutputPage } from './pages/OutputPage.js';
import { ParkingLotPage } from './pages/ParkingLotPage.js';
import { OutputsPage } from './pages/OutputsPage.js';
import { RatedRepliesPage } from './pages/RatedRepliesPage.js';
import { ProjectPage } from './pages/ProjectPage.js';
import { GuestInvitationPage } from './pages/GuestInvitationPage.js';
import { RecordingPage } from './pages/RecordingPage.js';
import { RoomPage } from './pages/RoomPage.js';
import { MeetingEntryPage } from './pages/MeetingEntryPage.js';
import { ScheduledMeetingsPage } from './pages/ScheduledMeetingsPage.js';
import { RoomRedirect } from './pages/RoomRedirect.js';
import { RecordingsPage } from './pages/RecordingsPage.js';
import { UsersPage } from './pages/UsersPage.js';
import { LabelPage } from './pages/LabelPage.js';
import { StatePage } from './pages/StatePage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { MeetingsPage } from './pages/MeetingsPage.js';
import { DrawerProvider } from './state/drawer.js';
import { HealthProvider } from './state/health.js';
import { SessionProvider, useSession } from './state/session.js';

function RequireAuthContent({ children }: { children: ReactNode }) {
  const { user, loading, setUser } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const embed = useEmbed();

  useEffect(
    () =>
      onUnauthorized(() => {
        setUser(null);
        if (!embed) navigate('/login', { replace: true });
      }),
    [navigate, setUser, embed],
  );

  if (loading) {
    return (
      <div className="page">
        <Spinner label="Signing in" />
      </div>
    );
  }
  if (!user && embed) return <EmbedSignIn />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return (
    <HealthProvider enabled>
      <DrawerProvider>{children}</DrawerProvider>
    </HealthProvider>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  return <><EmbedStatus /><RequireAuthContent>{children}</RequireAuthContent></>;
}

function AppShell() {
  const embed = useEmbed();
  return <><Layout />{!embed ? <FeedbackOverlay /> : null}</>;
}

// Keyed by their id so a switch remounts the page: no chat stream, error or half-loaded list from the
// previous project or thread can land in the new one.
function ProjectRoute() {
  const { projectId } = useParams();
  return <ProjectPage key={projectId} />;
}

function ConversationRoute() {
  const { conversationId } = useParams();
  return <ConversationPage key={conversationId} />;
}

function ScheduledMeetingsRoute() {
  const { projectId } = useParams();
  return <ScheduledMeetingsPage key={projectId} />;
}

function MeetingEntryRoute() {
  const { meetingId } = useParams();
  return <MeetingEntryPage key={meetingId} />;
}

const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/privacy', element: <PublicPolicyPage /> },
  { path: '/account-deletion', element: <PublicPolicyPage deletion /> },
  { path: '/guest-invitation/:invitationId', element: <GuestInvitationPage /> },
  { path: '/meeting-entry/:meetingId', element: <MeetingEntryRoute /> },
  {
    path: '/',
    element: (
      <RequireAuth>
        <MeetingRecorderProvider>
          <CompanionProvider>
            <AppShell />
          </CompanionProvider>
        </MeetingRecorderProvider>
      </RequireAuth>
    ),
    children: [
      { index: true, element: <HomePage /> },
      { path: 'projects', element: <ProjectsPage /> },
      { path: 'projects/new', element: <NewProjectPage /> },
      { path: 'p/:projectId', element: <ProjectRoute /> },
      { path: 'p/:projectId/scheduled-meetings', element: <ScheduledMeetingsRoute /> },
      { path: 'p/:projectId/c/:conversationId', element: <ConversationRoute /> },
      { path: 'p/:projectId/knows', element: <KnowsPage /> },
      { path: 'p/:projectId/context', element: <ContextPage /> },
      { path: 'p/:projectId/state', element: <StatePage /> },
      { path: 'p/:projectId/memory', element: <MemoryPage /> },
      { path: 'p/:projectId/outputs', element: <OutputsPage /> },
      { path: 'p/:projectId/outputs/:turnId', element: <OutputPage /> },
      { path: 'p/:projectId/rated', element: <RatedRepliesPage /> },
      { path: 'p/:projectId/recordings', element: <RecordingsPage /> },
      { path: 'r/:recordingId', element: <RecordingPage /> },
      { path: 'meet/:meetingId', element: <RoomPage /> },
      { path: 'room/:conversationId', element: <RoomRedirect /> },
      { path: 'parking-lot/:itemId?', element: <ParkingLotPage /> },
      { path: 'users', element: <UsersPage /> },
      { path: 'settings', element: <SettingsPage /> },
      { path: 'meetings', element: <MeetingsPage /> },
      { path: 'label', element: <LabelPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);

export function App() {
  return (
    <SessionProvider>
      <RouterProvider router={router} />
    </SessionProvider>
  );
}
